import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { loadOnboardingStatusForContext } from "@/server/route-guard";
import {
  resolveRedirectTargetEnhanced,
  resolveTargetPath,
} from "@/lib/routing/onboarding-redirect";
import { getUserWorkspacesServer } from "@/server/user-workspaces";
import { getOnboardingWizardServer } from "@/server/onboarding";
import { buildWorkspaceNewTaskReturnTo, parseNewTaskSearch } from "@/lib/routing/auth-return";
import { logInfo, logWarn } from "@/lib/structured-log";
import { WorkspaceOnboardingWizard } from "@/components/onboarding/workspace-onboarding-wizard";
import { AppSidebar } from "@/components/app-sidebar";
import { NewTaskPage } from "@/components/campaign/new-task-page";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { WorkspaceUserProvider } from "@/components/workspace/workspace-user-context";
import type { UserWorkspaceItem } from "@/lib/api/generated";

export const Route = createFileRoute("/workspace/new-task")({
  component: WorkspaceNewTaskEntry,
  validateSearch: z.object({
    prompt: z.string().optional(),
    agent: z.string().optional(),
  }),
  beforeLoad: () => loadOnboardingStatusForContext(),
  loader: async ({ context, location }) => {
    const search = parseNewTaskSearch(location.search);
    const { onboardingStatus } = context;
    if (!onboardingStatus) {
      throw redirect({ to: "/signin" });
    }

    const target = resolveRedirectTargetEnhanced(onboardingStatus, location.pathname);
    if (target) {
      const returnTo = buildWorkspaceNewTaskReturnTo(search);
      const desiredPath = resolveTargetPath(target);
      logInfo("workspace.new_task.redirect_to_onboarding", {
        from: location.pathname,
        to: desiredPath,
        state: onboardingStatus.onboarding_state,
        has_prompt: Boolean(search.prompt?.trim()),
      });
      throw redirect({
        ...target,
        search: target.to.startsWith("/onboarding") ? { return_to: returnTo } : undefined,
      });
    }

    if (
      (onboardingStatus.onboarding_flow_version ?? 1) >= 2 &&
      !onboardingStatus.onboarding_completed
    ) {
      const wizard = await getOnboardingWizardServer();
      if (wizard.workspace_slug) {
        throw redirect({
          to: "/workspace/$workspaceSlug/new-task",
          params: { workspaceSlug: wizard.workspace_slug },
          search,
        });
      }
      return {
        wizard,
        userDisplayName: onboardingStatus.user_display_name ?? null,
        userLanguage: onboardingStatus.user_language ?? null,
      };
    }

    const workspacesData = await getUserWorkspacesServer();
    const targetWorkspace = workspacesData.workspaces[0];

    if (!targetWorkspace?.slug) {
      logWarn("workspace.new_task.no_accessible_workspace", {
        workspace_count: workspacesData.workspaces.length,
      });
      throw redirect({ to: "/onboarding/profile" });
    }

    logInfo("workspace.new_task.redirect_to_first_workspace", {
      workspace_slug: targetWorkspace.slug,
    });

    throw redirect({
      to: "/workspace/$workspaceSlug/new-task",
      params: { workspaceSlug: targetWorkspace.slug },
      search,
    });
  },
});

function WorkspaceNewTaskEntry() {
  const { wizard, userDisplayName, userLanguage } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const initialAgentMode = search.agent === "sales" ? "sales" : "sourcing";
  const sidebarUserName = wizard.person.name?.trim() || userDisplayName?.trim() || "";
  const previewWorkspaces: UserWorkspaceItem[] = wizard.company.company_name
    ? [
        {
          display_name: wizard.company.company_name,
          id: "onboarding-preview",
          is_owner: true,
          logo_url: null,
          role: "owner",
          slug: "default",
        },
      ]
    : [];

  return (
    <div className="relative h-screen overflow-hidden bg-white">
      <div aria-hidden="true" className="pointer-events-none h-full select-none" inert>
        <SidebarProvider defaultOpen={true}>
          <div className="group flex h-screen w-full flex-col">
            <div className="flex flex-1 overflow-hidden">
              <AppSidebar
                userName={sidebarUserName || undefined}
                userLanguage={userLanguage ?? undefined}
                workspaceId=""
                userWorkspaces={previewWorkspaces}
                billingSummary={null}
                agentSessions={[]}
                onUpgradeClick={() => undefined}
              />
              <SidebarInset className="relative dot-pattern-bg flex flex-col">
                <div className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:hidden">
                  <SidebarTrigger />
                </div>
                <WorkspaceUserProvider displayName={sidebarUserName}>
                  <div className="relative z-10 flex-1 overflow-auto">
                    <NewTaskPage
                      workspaceId=""
                      workspaceSlug="default"
                      knowledgeProfile={null}
                      billingSummary={null}
                      initialPrompt={search.prompt}
                      initialAgentMode={initialAgentMode}
                    />
                  </div>
                </WorkspaceUserProvider>
              </SidebarInset>
            </div>
          </div>
        </SidebarProvider>
      </div>
      <WorkspaceOnboardingWizard
        initialWizard={wizard}
        onCompleted={(completedWizard) => {
          if (!completedWizard.workspace_slug) {
            return;
          }
          void navigate({
            to: "/workspace/$workspaceSlug/new-task",
            params: { workspaceSlug: completedWizard.workspace_slug },
            search,
            replace: true,
          });
        }}
      />
    </div>
  );
}
